import { useApp } from '@/app/AppContext'
import { AttachmentsPanel } from './AttachmentsPanel'
import { CustomFieldsPanel } from './CustomFieldsPanel'
import { HistoryPanel } from './HistoryPanel'
import type { CardTab, CardTabContext } from './types'

/**
 * Стандартные вкладки карточки (тикет #66, ТЗ §5.1).
 *
 * Вкладки — данные, а не ветвление внутри каркаса: конкретная карточка добавляет свои
 * через `extraTabs`, не трогая общий код.
 *
 * ## Про заглушки на этой странице
 *
 * Заглушка остаётся одна, и это написано **на экране**, а не только здесь.
 *
 * - **История** — журнал `crm.audit_log` (#104): кто, когда, что было и стало. Право —
 *   чтение самой записи; поле без полевого права приходит без значений.
 * - **Связанные** — требуют чтения соседних сущностей по ссылке, а REST над бизнес-логикой
 *   выводится в #67/#68 и на момент этой задачи не готов.
 * - **Вложения** — реализованы общим API и наследуют права родительской сущности.
 *
 * Пустой список вместо честной надписи — худший из вариантов: он выглядит рабочим и
 * находится через полгода не тем человеком, который и потратит день, выясняя, почему
 * «аудит не пишется».
 */

/** Вкладка с честной надписью о том, чего ещё нет. */
function NotImplemented({ messageKey, reasonKey }: { messageKey: string; reasonKey: string }) {
  const { t } = useApp()
  return (
    <div className="card-gap">
      <p>{t(messageKey)}</p>
      <p className="muted">{t(reasonKey)}</p>
    </div>
  )
}

/** История изменений записи (#104). */
export function historyTab(): CardTab {
  return {
    id: 'history',
    labelKey: 'card.tab.history',
    render: (context) => <HistoryPanel entity={context.entity} recordId={context.recordId} />,
  }
}

/** Связанные сущности. Заглушка: REST над бизнес-логикой выводится в #67/#68. */
export function relatedTab(): CardTab {
  return {
    id: 'related',
    labelKey: 'card.tab.related',
    render: () => (
      <NotImplemented messageKey="card.related.missing" reasonKey="card.related.reason" />
    ),
  }
}

/** Пользовательские поля записи (#106): объявляет администратор, заполняют на карточке. */
export function customFieldsTab(): CardTab {
  return {
    id: 'custom-fields',
    labelKey: 'card.tab.customFields',
    render: (context) => (
      <CustomFieldsPanel entity={context.entity} recordId={context.recordId} resource={context.resource} />
    ),
  }
}

/** Вложения: до clean-вердикта ClamAV строка видна, но файл не скачивается. */
export function attachmentsTab(): CardTab {
  return {
    id: 'attachments',
    labelKey: 'card.tab.attachments',
    render: (context) => (
      <AttachmentsPanel
        entityType={context.entity}
        entityId={context.recordId}
        resource={context.resource}
      />
    ),
  }
}

/**
 * Стандартный набор вкладок сверх «Основного».
 *
 * Порядок фиксирован: история идёт раньше вложений, потому что к ней возвращаются чаще —
 * вопрос «кто это поменял» задают каждый день, вопрос «где скан договора» реже.
 */
export function standardTabs(extra: CardTab[] = []): CardTab[] {
  return [relatedTab(), customFieldsTab(), historyTab(), attachmentsTab(), ...extra]
}

/** Явный реэкспорт типа: вкладки объявляют его, а импортировать удобнее отсюда. */
export type { CardTabContext }
